import { FormControlLabel, Switch } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { SIZES } from '../../../../../../constants/constants';
import { FORM_WIDGET_COMPONENTS } from '../../../../../../form/formConstants';
import { FormContainer } from '../../../../../../form/FormContainer';
import FormItem from '../../../../../../form/FormItem';
import FormPanel from '../../../../../../form/FormPanel';
import FormWidget from '../../../../../../form/FormWidget';
import I18n, { useI18n } from '../../../../../../i18n/I18n';
import type { GridSettingsDraft, GridSettingsSortField, GridSettingsSortOption } from '../../../types';
import { hasSelectedValueOrder, isCustomSortId, optionsForIds, sortFieldsForIds, toSelectOptions } from '../../utils';
import { ValueOrderField } from './ValueOrderField';

const SETTINGS_SWITCH_SX = { m: 0 };

type GridSettingsFormProps = {
    entityName?: string;
    showSortGroup: boolean;
    showAutosize: boolean;
    sortOptions: GridSettingsSortOption[];
    sortFields: GridSettingsSortField[];
    inGroupSortOptions: GridSettingsSortOption[];
    draft: GridSettingsDraft;
    customRemovalPending: boolean;
    onSortByChange: (name: string, selectedValue: unknown) => void;
    onSortFieldsChange: (name: string, selectedValue: unknown) => void;
    onValueOrderChange: (fieldId: string, values: string[]) => void;
    onInGroupSortChange: (name: string, selectedValue: string | null) => void;
    onGroupedFieldChange: (fieldId: string, checked: boolean) => void;
    onAutosizeChange: (event: unknown, checked: boolean) => void;
};

export function GridSettingsForm({
    entityName,
    showSortGroup,
    showAutosize,
    sortOptions,
    sortFields,
    inGroupSortOptions,
    draft,
    customRemovalPending,
    onSortByChange,
    onSortFieldsChange,
    onValueOrderChange,
    onInGroupSortChange,
    onGroupedFieldChange,
    onAutosizeChange
}: GridSettingsFormProps) {
    const t = useI18n();
    const sortByOptions = useMemo(() => toSelectOptions(sortOptions), [sortOptions]);
    const sortFieldOptions = useMemo(() => toSelectOptions(sortFields), [sortFields]);
    const inGroupOptions = useMemo(() => toSelectOptions(inGroupSortOptions), [inGroupSortOptions]);
    const showCustomFields = isCustomSortId(draft.sortBy) || customRemovalPending;
    const selectedSortFields = sortFieldsForIds(sortFields, draft.sortFields);
    const selectedSortFieldOptions = useMemo(
        () => optionsForIds(draft.sortFields, sortFieldOptions),
        [draft.sortFields, sortFieldOptions]
    );
    const showInGroupSort =
        showCustomFields && hasSelectedValueOrder(draft.valueOrders, selectedSortFields) && inGroupOptions.length > 0;

    return (
        <FormPanel>
            <FormContainer>
                {showSortGroup ? (
                    <FormItem>
                        <FormWidget
                            name="sortBy"
                            label={
                                entityName ? (
                                    <I18n token="form.field.sort.by" values={{ entity: entityName }} />
                                ) : (
                                    t('grid.settings.sort.by')
                                )
                            }
                            component={FORM_WIDGET_COMPONENTS.autocomplete}
                            options={sortByOptions}
                            value={draft.sortBy}
                            disableClearable
                            onChange={onSortByChange}
                        />
                    </FormItem>
                ) : null}
                {showSortGroup && showCustomFields ? (
                    <FormItem>
                        <FormWidget
                            name="sortFields"
                            label={t('form.field.group.by')}
                            component={FORM_WIDGET_COMPONENTS.autocomplete}
                            options={sortFieldOptions}
                            value={selectedSortFieldOptions}
                            multiple
                            formHelperText={<I18n token="report.field.group.description" />}
                            onChange={onSortFieldsChange}
                        />
                    </FormItem>
                ) : null}
                <>
                    {showSortGroup && showCustomFields
                        ? selectedSortFields.map((sortField) => (
                              <Fragment key={sortField.id}>
                                  <ValueOrderField
                                      sortField={{
                                          ...sortField,
                                          rankOrderLabel:
                                              sortField.rankOrderLabel ||
                                              t('grid.settings.group.value.order', {
                                                  field: sortField.label
                                              })
                                      }}
                                      value={draft.valueOrders[sortField.id] || []}
                                      onChange={onValueOrderChange}
                                  />
                                  <FormItem>
                                      <FormControlLabel
                                          data-testid={`dataGridGroupByOption-${sortField.id}`}
                                          sx={SETTINGS_SWITCH_SX}
                                          control={<Switch size={SIZES.small} />}
                                          label={t('grid.settings.group.by.field', { field: sortField.label })}
                                          checked={draft.groupedFields.includes(sortField.id)}
                                          onChange={(_event, checked) => onGroupedFieldChange(sortField.id, checked)}
                                      />
                                  </FormItem>
                              </Fragment>
                          ))
                        : null}
                </>
                {showSortGroup && showInGroupSort ? (
                    <FormItem>
                        <FormWidget
                            name="inGroupSortBy"
                            label={
                                entityName ? (
                                    <I18n token="form.field.sort.cohorts.by" values={{ entity: entityName }} />
                                ) : (
                                    t('grid.settings.sort.inGroup')
                                )
                            }
                            component={FORM_WIDGET_COMPONENTS.autocomplete}
                            options={inGroupOptions}
                            value={draft.inGroupSortBy}
                            disableClearable
                            onChange={onInGroupSortChange}
                        />
                    </FormItem>
                ) : null}
                {showAutosize ? (
                    <FormItem>
                        <FormControlLabel
                            data-testid="dataGridAutosizeOption"
                            sx={SETTINGS_SWITCH_SX}
                            control={<Switch size={SIZES.small} />}
                            label={t('grid.settings.autosize')}
                            checked={draft.autosizeEnabled}
                            onChange={onAutosizeChange}
                        />
                    </FormItem>
                ) : null}
            </FormContainer>
        </FormPanel>
    );
}
